import React, { useState, useMemo, useEffect } from 'react';
import { Search, Filter, Users, List, Tag, ChevronLeft, ChevronRight, CheckSquare, Square } from 'lucide-react';
import { ZONAS_MALAGA, normalizarZonaMalaga } from '../utils/zonasMalaga';

const AdvancedContactSelector = ({
    contactosActivos,
    listasCompletadas,
    fetchContactosDeLista,
    contactosSeleccionados, // Array of IDs
    setContactosSeleccionados // function to update Array of IDs
}) => {
    const [activeTab, setActiveTab] = useState('todos'); // 'todos', 'listas', 'categorias'
    const [searchTerm, setSearchTerm] = useState('');
    const [filtroZonaMalaga, setFiltroZonaMalaga] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [filtros, setFiltros] = useState({}); // { campo: valor }
    
    // Para las listas cargadas desde el backend
    const [listasCache, setListasCache] = useState({}); // { lista_id: [contacto_ids] }
    const [listaActiva, setListaActiva] = useState(null); // lista_id
    const [cargandoLista, setCargandoLista] = useState(false);

    // Obtener campos únicos para filtros dinámicos (excluyendo IDs, zona y campos irrelevantes)
    const camposFiltro = useMemo(() => {
        const excluidos = ['id', 'nombre', 'email', 'telefono', 'whatsapp', 'fecha_creacion', 'activo', 'notas', 'intereses', 'zona', 'distrito_municipal'];
        const campos = {};
        contactosActivos.forEach(c => {
            Object.keys(c).forEach(key => {
                if (!excluidos.includes(key) && c[key]) {
                    if (!campos[key]) campos[key] = new Set();
                    campos[key].add(c[key]);
                }
            });
        });
        // Convertir Sets a Arrays
        const resultado = {};
        Object.keys(campos).forEach(k => {
            if (campos[k].size > 1) { // Solo mostrar filtro si hay más de 1 opción
                resultado[k] = Array.from(campos[k]).sort();
            }
        });
        return resultado;
    }, [contactosActivos]);

    // Cargar contactos de una lista
    const cargarLista = async (lista_id) => {
        if (listasCache[lista_id]) {
            setListaActiva(lista_id);
            return;
        }
        setCargandoLista(true);
        try {
            const data = await fetchContactosDeLista(lista_id, 1, 5000);
            if (data.success && data.contactos) {
                const ids = data.contactos.map(c => c.id);
                setListasCache(prev => ({ ...prev, [lista_id]: ids }));
                setListaActiva(lista_id);
            }
        } catch (e) {
            console.error('Error cargando lista', e);
        } finally {
            setCargandoLista(false);
        }
    };

    // Aplicar filtros y búsqueda
    const contactosFiltrados = useMemo(() => {
        return contactosActivos.filter(c => {
            // Filtro por pestaña de listas
            if (activeTab === 'listas' && listaActiva) {
                if (!listasCache[listaActiva]?.includes(c.id)) return false;
            }

            // Filtro por Zona de Málaga Responde
            if (filtroZonaMalaga) {
                const zonaNorm = normalizarZonaMalaga(c.zona || c.distrito_municipal || '');
                if (zonaNorm !== filtroZonaMalaga) return false;
            }

            // Búsqueda de texto
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                const coincide = 
                    (c.nombre || '').toLowerCase().includes(searchLower) ||
                    (c.email || '').toLowerCase().includes(searchLower) ||
                    (c.telefono || '').toLowerCase().includes(searchLower) ||
                    (c.zona || '').toLowerCase().includes(searchLower);
                if (!coincide) return false;
            }

            // Filtros dinámicos (solo en pestaña 'todos' o 'categorias')
            for (const [campo, valor] of Object.entries(filtros)) {
                if (valor && c[campo] !== valor) return false;
            }

            return true;
        });
    }, [contactosActivos, searchTerm, filtroZonaMalaga, filtros, activeTab, listaActiva, listasCache]);

    // Paginación
    const totalPages = Math.ceil(contactosFiltrados.length / itemsPerPage);
    const currentContacts = contactosFiltrados.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    useEffect(() => {
        setCurrentPage(1); // Reset page on filter change
    }, [searchTerm, filtroZonaMalaga, filtros, activeTab, listaActiva]);

    // Handlers de selección
    const toggleSeleccionMultiple = (ids, select) => {
        const current = new Set(contactosSeleccionados);
        ids.forEach(id => select ? current.add(id) : current.delete(id));
        setContactosSeleccionados(Array.from(current));
    };

    const isAllCurrentSelected = currentContacts.length > 0 && currentContacts.every(c => contactosSeleccionados.includes(c.id));
    const isAllFilteredSelected = contactosFiltrados.length > 0 && contactosFiltrados.every(c => contactosSeleccionados.includes(c.id));

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Cabecera / Pestañas */}
            <div className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <button 
                    onClick={() => { setActiveTab('todos'); setListaActiva(null); }}
                    className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'todos' ? 'text-primary-600 border-b-2 border-primary-600 bg-white dark:bg-gray-800' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                >
                    <Users size={18} /> Todos los Contactos
                </button>
                <button 
                    onClick={() => setActiveTab('listas')}
                    className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${activeTab === 'listas' ? 'text-emerald-600 border-b-2 border-emerald-600 bg-white dark:bg-gray-800' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                >
                    <List size={18} /> Por Listas de Importación
                </button>
            </div>

            <div className="p-4 space-y-4">
                {/* Panel de Listas (Solo visible en pestaña listas) */}
                {activeTab === 'listas' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                        {listasCompletadas.length === 0 && <p className="text-sm text-gray-500 col-span-full">No hay listas disponibles.</p>}
                        {listasCompletadas.map(l => (
                            <button
                                key={l.id}
                                onClick={() => cargarLista(l.id)}
                                className={`flex flex-col text-left p-3 rounded-lg border transition ${listaActiva === l.id ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-500' : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-emerald-300'}`}
                            >
                                <span className="text-sm font-bold text-gray-800 dark:text-gray-200">{l.nombre}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">{l.total_contactos} contactos</span>
                            </button>
                        ))}
                    </div>
                )}

                {/* Buscador y Filtros */}
                <div className="flex flex-col md:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Buscar por nombre, email, teléfono o zona..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary-500"
                        />
                    </div>
                    {/* Selector oficial de las 14 Zonas de Málaga Responde */}
                    <select
                        value={filtroZonaMalaga}
                        onChange={e => setFiltroZonaMalaga(e.target.value)}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-700 dark:text-gray-200 font-medium"
                    >
                        <option value="">Todas las Zonas de Málaga (14)</option>
                        {ZONAS_MALAGA.map(z => (
                            <option key={z} value={z}>{z}</option>
                        ))}
                    </select>
                    {activeTab === 'todos' && Object.keys(camposFiltro).length > 0 && (
                        <div className="flex gap-2 overflow-x-auto pb-1">
                            {Object.entries(camposFiltro).slice(0, 2).map(([campo, opciones]) => (
                                <select
                                    key={campo}
                                    value={filtros[campo] || ''}
                                    onChange={e => setFiltros(prev => ({ ...prev, [campo]: e.target.value }))}
                                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-700 dark:text-gray-200"
                                >
                                    <option value="">Todos los {campo.replace('_', ' ')}</option>
                                    {opciones.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                                </select>
                            ))}
                        </div>
                    )}
                </div>

                {/* Acciones Rápidas */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-gray-50 dark:bg-gray-900/30 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                        Mostrando <span className="font-bold text-gray-900 dark:text-white">{contactosFiltrados.length}</span> resultados.
                    </div>
                    <button
                        onClick={() => toggleSeleccionMultiple(contactosFiltrados.map(c => c.id), !isAllFilteredSelected)}
                        className="text-sm font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center gap-2"
                        disabled={contactosFiltrados.length === 0}
                    >
                        {isAllFilteredSelected ? <CheckSquare size={18} /> : <Square size={18} />}
                        {isAllFilteredSelected ? 'Deseleccionar estos resultados' : 'Seleccionar estos resultados'}
                    </button>
                </div>

                {/* Tabla de Contactos */}
                <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                    {cargandoLista ? (
                        <div className="p-8 text-center text-gray-500">Cargando lista...</div>
                    ) : (
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-600 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700">
                                <tr>
                                    <th className="p-3 w-10 text-center">
                                        <input
                                            type="checkbox"
                                            checked={isAllCurrentSelected && currentContacts.length > 0}
                                            onChange={() => toggleSeleccionMultiple(currentContacts.map(c => c.id), !isAllCurrentSelected)}
                                            className="w-4 h-4 text-primary-600 rounded border-gray-300"
                                        />
                                    </th>
                                    <th className="p-3">Nombre</th>
                                    <th className="p-3">Contacto</th>
                                    <th className="p-3">Detalles</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {currentContacts.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="p-8 text-center text-gray-500 dark:text-gray-400">
                                            No se encontraron contactos que coincidan con la búsqueda.
                                        </td>
                                    </tr>
                                ) : (
                                    currentContacts.map(c => {
                                        const isSelected = contactosSeleccionados.includes(c.id);
                                        return (
                                            <tr key={c.id} className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 transition ${isSelected ? 'bg-primary-50/50 dark:bg-primary-900/10' : ''}`}>
                                                <td className="p-3 text-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleSeleccionMultiple([c.id], !isSelected)}
                                                        className="w-4 h-4 text-primary-600 rounded border-gray-300"
                                                    />
                                                </td>
                                                <td className="p-3 font-medium text-gray-900 dark:text-gray-100">
                                                    {c.nombre || '-'}
                                                </td>
                                                <td className="p-3 text-gray-600 dark:text-gray-400">
                                                    <div>{c.email}</div>
                                                    <div className="text-xs">{c.telefono}</div>
                                                </td>
                                                <td className="p-3 text-gray-600 dark:text-gray-400">
                                                    <div className="flex flex-wrap gap-1">
                                                        {c.tipo_usuario && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded text-xs">{c.tipo_usuario}</span>}
                                                        {c.empresa && <span className="px-2 py-0.5 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded text-xs">{c.empresa}</span>}
                                                        {(c.zona || c.distrito_municipal) && (
                                                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 rounded text-xs">
                                                                {normalizarZonaMalaga(c.zona || c.distrito_municipal)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Paginación */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                            Página {currentPage} de {totalPages}
                        </span>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                                <ChevronLeft size={18} />
                            </button>
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                                <ChevronRight size={18} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdvancedContactSelector;
